import { useEffect, useState, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { toast } from '../lib/toast.js';
import { useRolePerms } from '../features/auth/useRolePerms.js';
import { useResetSub } from '../features/filters/useFilters.js';
import { AddCertModal } from '../components/domain/AddCertModal.js';
import { CertNotifyModal } from '../components/domain/CertNotifyModal.js';
import { RoleManagerModal } from '../components/domain/RoleManagerModal.js';
import { TrainExportModal } from '../components/domain/TrainExportModal.js';
import { AddRoleReqModal } from '../components/domain/AddRoleReqModal.js';
import { UploadModal } from '../components/domain/UploadModal.js';
import { WorkerDetail } from '../components/domain/WorkerDetail.js';
import { EditCertModal } from '../components/domain/EditCertModal.js';
import { ShareTrainingModal } from '../components/domain/ShareTrainingModal.js';
import { RtwBadge } from '../components/primitives/RtwBadge.js';
import type { RtwStatus, TrainingMatrixDescriptor } from '@cls/shared';
import {
  buildCategoryGroups,
  buildRequiredForRole,
  certMatchesColumn,
  columnMandatoryForWorker,
  computeColStats,
  computeMatrixKpis,
  filterMatrixWorkers,
  findCscs,
  formatMatrixExpiry,
  fuzzyMatchLegacyRow,
  groupRowsByProject,
  isRecentCert,
  matrixCscsState,
  matrixWeekEndingLabel,
  selectTrainColumns,
  sortMatrixWorkers,
} from '@cls/shared';

interface Deployment { workerId: string; projectId: string; projectName: string | null; status: string; }

interface CertEntry {
  id?: string;
  type: string;
  expiry: string | null;
  status: 'valid' | 'expiring' | 'expired';
  notes?: string | null;
  addedAt?: string | null;
  docRef?: string | null;
  docUrl?: string | null;
}

interface MxWorker {
  id: string;
  name: string;
  role: string;
  roles: string[];
  core: boolean;
  rating: number | null;
  agencyCode: string | null;
  rtwStatus?: string | null;
  rtwExpiry?: string | null;
  certs: CertEntry[];
}

interface MxRow {
  id: string;
  fn: string;
  ln: string;
  trade: string | null;
  ag: string | null;
  workerId: string | null;
  certs: Record<string, string | null>;
}

interface MxResponse {
  certColumns: Array<{ key: string; label: string; group?: string }>;
  mxRows: MxRow[];
  workers: MxWorker[];
  filter: { addedWithinDays: number | null };
}

interface RoleRequirement {
  role: string;
  mandatory: string[];
  beneficial: string[];
  other: string[];
  displayOrder?: number;
}

// Group label → CSS class for the matrix header. Unknown groups fall through to mx-grp-trade.
const GROUP_CSS: Record<string, string> = {
  MANDATORY: 'mx-grp-mandatory',
  BENEFICIAL: 'mx-grp-mgr',
  OTHER: 'mx-grp-trade',
};

const MX_TRADE_CLS: Record<string, string> = {
  'TRAFFIC MARSHALL': 'mx-tr-TM', 'TRAFFIC MARSHAL': 'mx-tr-TM', 'LABOURER': 'mx-tr-LAB', 'SECURITY': 'mx-tr-SEC',
  'LOGISTICS MANAGER': 'mx-tr-LM', 'WELFARE': 'mx-tr-WEL', 'DRIVER': 'mx-tr-DRV',
  'CLEANER': 'mx-tr-CLN', 'SLINGER': 'mx-tr-SLG', 'HQ MANAGER': 'mx-tr-HQM',
  'HANDYMAN': 'mx-tr-HMN', 'CARPENTER': 'mx-tr-CRP', 'IPAF': 'mx-tr-IPF',
  'BEAM HOIST DRIVER': 'mx-tr-BHD', 'PAINTER': 'mx-tr-PNT', 'CRANE SUPERVISOR': 'mx-tr-CS',
};

function tradeCls(role: string | null | undefined): string {
  if (!role) return '';
  return MX_TRADE_CLS[role.toUpperCase()] ?? '';
}


export function Training() {
  const qc = useQueryClient();
  const { canImport, canExport, canBroadcast, canEditWorkers, isInternal } = useRolePerms();

  const [mxv, setMxv] = useState<'all' | 'deployed-by-project' | 'deployed-flat'>('all');
  const [search, setSearch] = useState('');
  const [fTrade, setFTrade] = useState('');
  const [fAgency, setFAgency] = useState('');
  const [fExpiring, setFExpiring] = useState(false);
  const [fExpired, setFExpired] = useState(false);
  const [fMissing, setFMissing] = useState(false);
  const [fVisaExpiring, setFVisaExpiring] = useState(false);
  const [recentActive, setRecentActive] = useState(false);
  const [recentDays, setRecentDays] = useState(30);
  const [recentMode, setRecentMode] = useState<'full' | 'condensed'>('full');

  const [sortBy, setSortBy] = useState<'name' | string>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const [addCertOpen, setAddCertOpen] = useState(false);
  const [addCertFor, setAddCertFor] = useState<{ workerId: string; workerName: string; certType?: string } | null>(null);
  const [editCert, setEditCert] = useState<{ certId: string; workerName: string; initial: { type: string; expiry: string | null; status: 'valid' | 'expiring' | 'expired'; notes: string | null; docUrl: string | null } } | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [shareWorkerId, setShareWorkerId] = useState<string | null>(null);
  const [roleMgrOpen, setRoleMgrOpen] = useState(false);
  const [roleMgrEditRole, setRoleMgrEditRole] = useState<string | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [addReqOpen, setAddReqOpen] = useState(false);
  const [addReqInitial, setAddReqInitial] = useState<{ role?: string; category?: 'mandatory' | 'beneficial' } | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const [notifyOpen, setNotifyOpen] = useState(searchParams.get('notify') === '1');
  const [notifyMissingOpen, setNotifyMissingOpen] = useState(false);

  const matrixWrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (searchParams.get('notify') === '1') {
      setNotifyOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('notify');
      setSearchParams(next, { replace: true });
    }
    if (searchParams.get('expiring') === '1') {
      setFExpiring(true);
      setRecentActive(false);
      const next = new URLSearchParams(searchParams);
      next.delete('expiring');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useResetSub(() => {
    setSearch(''); setFTrade(''); setFAgency(''); setFExpiring(false); setFExpired(false); setFMissing(false);
    setFVisaExpiring(false);
    setRecentActive(false); setRecentDays(30); setRecentMode('full');
  });

  const { data: matrix } = useQuery({
    queryKey: ['training', 'matrix', recentDays],
    queryFn: () => api.get<MxResponse>(`/training/matrix${recentActive ? `?addedWithinDays=${recentDays}` : ''}`).then((r) => r.data),
  });
  const { data: roleReqs = [] } = useQuery({
    queryKey: ['training', 'role-requirements'],
    queryFn: () => api.get<RoleRequirement[]>('/training/role-requirements').then((r) => r.data),
  });
  const { data: deployments = [] } = useQuery({
    queryKey: ['deployments'],
    queryFn: () => api.get<Deployment[]>('/deployments').then((r) => r.data),
  });

  const workers = matrix?.workers ?? [];
  const mxRows = matrix?.mxRows ?? [];
  const allCertCols = matrix?.certColumns ?? [];
  // Skip rows in the STICKY group — those are surfaced as sticky columns (CSCS), not matrix cells.
  const trainCols = useMemo(() => selectTrainColumns(allCertCols), [allCertCols]);

  const tradeOptions = useMemo(() => [...new Set(workers.map((w) => w.role).filter(Boolean))].sort(), [workers]);
  const agencyOptions = useMemo(() => [...new Set(workers.map((w) => w.agencyCode).filter(Boolean) as string[])].sort(), [workers]);

  const activeDeployments = useMemo(() => deployments.filter((d) => d.status === 'active'), [deployments]);
  const deployedWorkerIds = useMemo(() => new Set(activeDeployments.map((d) => d.workerId)), [activeDeployments]);
  const workerProject = useMemo(() => {
    const m: Record<string, { id: string; name: string }> = {};
    for (const d of activeDeployments) {
      if (!m[d.workerId]) m[d.workerId] = { id: d.projectId, name: d.projectName ?? d.projectId };
    }
    return m;
  }, [activeDeployments]);

  const requiredForRole = useMemo(() => buildRequiredForRole(roleReqs), [roleReqs]);

  const visibleGroups = useMemo(
    () => buildCategoryGroups(trainCols, { roleRequirements: roleReqs, trade: fTrade, workers })
      .map((g) => ({ ...g, cssClass: GROUP_CSS[g.label] ?? 'mx-grp-trade' })),
    [trainCols, roleReqs, fTrade, workers],
  );

  // trainCols ordered to match the visibleGroups header (already in API order, but flatten via groups for safety)
  const orderedTrainCols = useMemo(() => visibleGroups.flatMap((g) => g.cols), [visibleGroups]);

  const descriptor = useMemo<TrainingMatrixDescriptor>(() => ({
    view: mxv,
    filters: {
      search,
      trade: fTrade,
      agency: fAgency,
      expiring: fExpiring,
      expired: fExpired,
      missing: fMissing,
      visaExpiring: fVisaExpiring,
      recentActive,
      recentDays,
    },
    sortBy,
    sortDir,
  }), [mxv, search, fTrade, fAgency, fExpiring, fExpired, fMissing, fVisaExpiring, recentActive, recentDays, sortBy, sortDir]);

  // Build the visible row list
  const filtered = useMemo(
    () => sortMatrixWorkers(
      filterMatrixWorkers(workers, descriptor, { deployedWorkerIds, requiredForRole }),
      sortBy,
      sortDir,
      orderedTrainCols,
    ),
    [workers, descriptor, deployedWorkerIds, requiredForRole, sortBy, sortDir, orderedTrainCols],
  );

  const groupedByProject = useMemo(() => {
    if (mxv !== 'deployed-by-project') return null;
    return groupRowsByProject(filtered, workerProject);
  }, [mxv, filtered, workerProject]);

  // Column compliance stats
  const colStats = useMemo(() => computeColStats(filtered, trainCols), [filtered, trainCols]);

  // KPI calculations
  const kpis = useMemo(() => computeMatrixKpis(workers, requiredForRole), [workers, requiredForRole]);
  const { totalCerts, validCerts, expiringCerts, expiredCerts, missingCount, visaExpiring, visaExpired } = kpis;
  const recentInfo = { workerCount: kpis.recentWorkerCount, certCount: kpis.recentCertCount };

  const kpiTiles = [
    {
      label: 'Missing Mandatory', val: missingCount, color: '#DC2626', sub: 'click to filter',
      onClick: () => { setFMissing(true); setFExpired(false); setFExpiring(false); setFVisaExpiring(false); setRecentActive(false); scrollMatrix(); },
    },
    {
      label: 'Expired Certs', val: expiredCerts, color: '#EF4444', sub: 'click to filter',
      onClick: () => { setFExpired(true); setFMissing(false); setFExpiring(false); setFVisaExpiring(false); setRecentActive(false); scrollMatrix(); },
    },
    {
      label: 'Expiring Soon', val: expiringCerts, color: '#F59E0B', sub: 'click to filter',
      onClick: () => { setFExpiring(true); setFMissing(false); setFExpired(false); setFVisaExpiring(false); setRecentActive(false); scrollMatrix(); },
    },
    {
      label: 'Recent Certs <30d', val: recentInfo.workerCount, color: '#22C55E', sub: `${recentInfo.certCount} cert${recentInfo.certCount !== 1 ? 's' : ''} added`,
      onClick: () => { setRecentActive(true); setRecentDays(30); setFExpired(false); setFExpiring(false); setFMissing(false); setFVisaExpiring(false); scrollMatrix(); },
    },
    {
      label: 'Visas Expiring', val: visaExpiring, color: '#8B5CF6', sub: visaExpired ? `${visaExpired} already expired` : 'within 60 days',
      onClick: () => { setFVisaExpiring(true); setFMissing(false); setFExpired(false); setFExpiring(false); setRecentActive(false); scrollMatrix(); },
    },
    {
      label: 'Valid Certs', val: validCerts, color: '#22C55E', sub: 'click to filter',
      onClick: () => { setFExpired(false); setFExpiring(false); setFMissing(false); setFVisaExpiring(false); setRecentActive(false); scrollMatrix(); },
    },
    {
      label: 'Total Certs', val: totalCerts, color: '#6B7A99', sub: 'click to clear filters',
      onClick: () => { setFExpired(false); setFExpiring(false); setFMissing(false); setFVisaExpiring(false); setRecentActive(false); setSearch(''); setFTrade(''); setFAgency(''); scrollMatrix(); },
    },
  ];

  function scrollMatrix() { matrixWrapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }

  function toggleSort(key: string) {
    if (sortBy === key) {
      if (sortDir === 'asc') setSortDir('desc');
      else { setSortBy('name'); setSortDir('asc'); }
    } else { setSortBy(key); setSortDir('asc'); }
  }
  function sortArrow(key: string): string {
    if (sortBy !== key) return '⇅';
    return sortDir === 'asc' ? '↑' : '↓';
  }

  const removeReqMut = useMutation({
    mutationFn: async (input: RoleRequirement) => api.put('/training/role-requirements', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['training', 'role-requirements'] });
      toast.info('Requirement removed');
    },
    onError: () => toast.error('Update failed'),
  });

  function removeReq(role: string, kind: 'mandatory' | 'beneficial' | 'other', cert: string) {
    const r = roleReqs.find((x) => x.role === role);
    if (!r) return;
    const next: RoleRequirement = {
      role,
      mandatory: kind === 'mandatory' ? r.mandatory.filter((c) => c !== cert) : r.mandatory,
      beneficial: kind === 'beneficial' ? r.beneficial.filter((c) => c !== cert) : r.beneficial,
      other: kind === 'other' ? r.other.filter((c) => c !== cert) : r.other,
      displayOrder: r.displayOrder,
    };
    removeReqMut.mutate(next);
  }

  function renderMxRow(w: MxWorker): JSX.Element {
    const mxRow = fuzzyMatchLegacyRow(w.name, mxRows);
    const { expiry: cscs } = findCscs(w, mxRow);
    const ag = w.agencyCode ?? mxRow?.ag ?? '';
    const tradeClass = tradeCls(w.role);

    return (
      <tr key={w.id} style={{ cursor: 'pointer' }}>
        <td className="mx-s1" onClick={() => setDetailId(w.id)} title="Open profile">
          <div className="mx-name-cell">
            <span className="mx-name-full">
              {w.name}
              {w.core ? <span className="pill" style={{ marginLeft: 6, background: 'rgba(232,164,39,.18)', color: 'var(--accent)', fontSize: 8, padding: '1px 5px' }}>CORE</span> : null}
              {w.rating ? <span style={{ marginLeft: 4, color: 'var(--accent)', fontSize: 9 }}>{'★'.repeat(w.rating)}{'☆'.repeat(Math.max(0, 5 - w.rating))}</span> : null}
              {fVisaExpiring ? <span style={{ marginLeft: 6 }}><RtwBadge status={w.rtwStatus as RtwStatus | null | undefined} expiry={w.rtwExpiry} /></span> : null}
            </span>
            <span className={`mx-trade-lbl ${tradeClass}`}>{w.role}</span>
          </div>
        </td>
        <td className="mx-s2 mx-agency-cell">
          {ag ? <span className={`mx-ag mx-ag-${ag}`}>{ag}</span> : <span style={{ color: 'var(--faint)' }}>—</span>}
        </td>
        <td className={`mx-s3 mx-cscs-cell mx-cscs-${matrixCscsState(cscs)}`}>
          {cscs ? formatMatrixExpiry(cscs) : '—'}
        </td>
        {orderedTrainCols.map((c) => {
          const cert = w.certs.find((x) => certMatchesColumn(x.type, c));
          const missing = !cert && columnMandatoryForWorker(w, c, requiredForRole);
          const status = cert?.status ?? (missing ? 'missing' : 'empty');
          const recentMark = recentActive && cert?.addedAt && isRecentCert(cert.addedAt, recentDays);
          return (
            <td
              key={c.key}
              className="mx-train-cell"
              onClick={(e) => {
                e.stopPropagation();
                if (cert && cert.id) {
                  setEditCert({
                    certId: cert.id,
                    workerName: w.name,
                    initial: { type: cert.type, expiry: cert.expiry, status: cert.status, notes: cert.notes ?? null, docUrl: cert.docUrl ?? null },
                  });
                } else {
                  setAddCertFor({ workerId: w.id, workerName: w.name, certType: c.label });
                }
              }}
              style={recentMark ? { boxShadow: 'inset 0 0 0 2px #22c55e', borderRadius: 4, position: 'relative' } : undefined}
              title={cert?.expiry ? `${c.label}: ${cert.expiry.slice(0, 10)} — click to edit` : missing ? `${c.label}: required for role — click to add` : `${c.label}: click to add`}
            >
              <span className={`mx-pill mx-pill-${status}`}>
                {cert?.expiry ? formatMatrixExpiry(cert.expiry) : missing ? 'MISSING' : '—'}
              </span>
              {recentMark ? (
                <span style={{ position: 'absolute', top: 1, right: 1, background: '#22c55e', color: '#0B0F1A', fontSize: 7, fontWeight: 800, padding: '1px 3px', borderRadius: 3, letterSpacing: '.4px', lineHeight: 1 }}>NEW</span>
              ) : null}
            </td>
          );
        })}
        <td className="mx-train-cell" onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
          <button className="btn btn-ghost btn-xs" onClick={() => setShareWorkerId(w.id)} title="Share training record" style={{ fontSize: 10 }}>
            📤 Share
          </button>
        </td>
      </tr>
    );
  }

  function renderCondensedRow(w: MxWorker): JSX.Element | null {
    const recent = w.certs.filter((c) => isRecentCert(c.addedAt, recentDays));
    if (!recent.length) return null;
    const tradeClass = tradeCls(w.role);
    const colCount = 3 + trainCols.length + 1;
    return (
      <tr key={w.id} style={{ background: 'rgba(34,197,94,.03)' }}>
        <td colSpan={colCount} style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }} onClick={() => setDetailId(w.id)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: 12 }}>{w.name}</span>
            <span className={`mx-trade-lbl ${tradeClass}`} style={{ fontSize: 9 }}>{w.role}</span>
            {w.agencyCode ? <span className={`mx-ag mx-ag-${w.agencyCode}`} style={{ fontSize: 9 }}>{w.agencyCode}</span> : null}
            <span style={{ flex: 1 }}></span>
            {recent.sort((a, b) => (b.addedAt ?? '').localeCompare(a.addedAt ?? '')).map((c) => (
              <span key={c.id ?? c.type} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(34,197,94,.12)', border: '1px solid rgba(34,197,94,.35)', borderRadius: 4, padding: '2px 7px', fontSize: 10, color: '#22c55e', fontWeight: 600, margin: '2px 3px 2px 0' }}>
                <span style={{ fontSize: 9, opacity: .85 }}>+</span>{c.type}
                <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 9 }}>{c.addedAt ? new Date(c.addedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : ''}</span>
              </span>
            ))}
          </div>
        </td>
      </tr>
    );
  }

  // Header + sub-text per prototype
  const sub = `${workers.length} workers · ${roleReqs.length} role${roleReqs.length !== 1 ? 's' : ''}`;
  const mxCount = `${filtered.length} of ${workers.length} records`;

  return (
    <div className="page" id="page-training">
      <div className="hbar">
        <div>
          <div className="ph">Training &amp; Compliance</div>
          <div className="ps" id="train-sub">{sub}</div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {canImport ? <button className="btn btn-ghost btn-sm" onClick={() => setImportOpen(true)}>⬆ Import</button> : null}
          {canExport ? <button className="btn btn-ghost btn-sm" onClick={() => setExportOpen(true)}>⬇ Export</button> : null}
          {canBroadcast ? <button className="btn btn-warn btn-sm" onClick={() => setNotifyOpen(true)}>🔔 Notify Expiring</button> : null}
          {canBroadcast ? <button className="btn btn-warn btn-sm" onClick={() => setNotifyMissingOpen(true)}>🔔 Notify Missing</button> : null}
          {isInternal ? <button className="btn btn-ghost btn-sm" onClick={() => { setRoleMgrEditRole(null); setRoleMgrOpen(true); }}>⚙ Manage Roles</button> : null}
          {canEditWorkers ? <button className="btn btn-primary btn-sm" onClick={() => setAddCertOpen(true)}>+ Record Certificate</button> : null}
        </div>
      </div>

      <div className="g4" style={{ marginBottom: 16 }} id="train-kpis">
        {kpiTiles.map((k) => (
          <div key={k.label} className="kpi" style={{ borderTopColor: k.color, cursor: 'pointer' }} onClick={k.onClick} title="Click to filter">
            <div className="lbl">{k.label}</div>
            <div className="val" style={{ color: k.color, fontSize: 19 }}>{k.val}</div>
            <div className="sub" style={{ color: k.color, fontSize: 9, opacity: .8 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
          <div className="sec-title" style={{ margin: 0 }}>Workforce Training Matrix</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div className="snav" style={{ padding: 3, gap: 3 }}>
              {([
                { k: 'all', lbl: 'All Workers' },
                { k: 'deployed-by-project', lbl: 'Deployed — by Project' },
                { k: 'deployed-flat', lbl: 'Deployed — Flat' },
              ] as const).map((v) => (
                <div key={v.k} className={`snav-btn ${mxv === v.k ? 'active' : ''}`} data-mxv={v.k} onClick={() => setMxv(v.k)}>
                  {v.lbl}
                </div>
              ))}
            </div>
            <span style={{ fontSize: 11, color: 'var(--muted)' }} id="mx-week-label">WE {matrixWeekEndingLabel()}</span>
          </div>
        </div>

        <div className="mx-fbar">
          <input id="mx-search" type="text" placeholder="Search name…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <select id="mx-trade" value={fTrade} onChange={(e) => setFTrade(e.target.value)}>
            <option value="">All Trades</option>
            {tradeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select id="mx-agency" value={fAgency} onChange={(e) => setFAgency(e.target.value)}>
            <option value="">All Agencies</option>
            {agencyOptions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <button className={`mx-fbtn ${fExpiring ? 'mx-factive' : ''}`} id="mx-btn-expiring" onClick={() => setFExpiring((v) => !v)}>⚠ Expiring &lt;90d</button>
          <button className={`mx-fbtn ${fExpired ? 'mx-factive' : ''}`} id="mx-btn-expired" onClick={() => setFExpired((v) => !v)}>✕ Expired</button>
          <button className={`mx-fbtn ${fMissing ? 'mx-factive' : ''}`} id="mx-btn-missing" onClick={() => setFMissing((v) => !v)}>○ Missing Induction</button>
          <button className={`mx-fbtn ${fVisaExpiring ? 'mx-factive' : ''}`} id="mx-btn-visa" onClick={() => setFVisaExpiring((v) => !v)} title="Workers whose visa is expired or expires within 60 days">🛂 Visa Expiring &lt;60d</button>
          <button className={`mx-fbtn ${recentActive ? 'mx-factive' : ''}`} id="mx-btn-recent" onClick={() => setRecentActive((v) => !v)} title="Workers with certificates added recently">
            <span>🆕 Added &lt;</span>
            <input
              type="number"
              id="mx-recent-days"
              min={1}
              max={365}
              value={recentDays}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setRecentDays(parseInt(e.target.value, 10) || 30)}
              style={{ width: 42, background: 'transparent', border: 'none', color: 'inherit', fontWeight: 700, textAlign: 'center', padding: 0, margin: '0 2px', outline: 'none' }}
            />
            <span>d</span>
          </button>
          {recentActive ? (
            <div id="mx-recent-mode" style={{ display: 'inline-flex', alignItems: 'center', gap: 2, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: 2 }}>
              <button className="mx-mbtn" id="mx-mode-full" onClick={() => setRecentMode('full')} style={{ padding: '4px 9px', fontSize: 10, border: 'none', background: recentMode === 'full' ? 'var(--accent)' : 'transparent', color: recentMode === 'full' ? '#0B0F1A' : 'var(--muted)', fontWeight: recentMode === 'full' ? 700 : 500, borderRadius: 4, cursor: 'pointer' }} title="Show full matrix row with new certs highlighted">Full row</button>
              <button className="mx-mbtn" id="mx-mode-condensed" onClick={() => setRecentMode('condensed')} style={{ padding: '4px 9px', fontSize: 10, border: 'none', background: recentMode === 'condensed' ? 'var(--accent)' : 'transparent', color: recentMode === 'condensed' ? '#0B0F1A' : 'var(--muted)', fontWeight: recentMode === 'condensed' ? 700 : 500, borderRadius: 4, cursor: 'pointer' }} title="Show condensed list of just the newly-added certs">Condensed</button>
            </div>
          ) : null}
          <span className="mx-count" id="mx-count">{mxCount}</span>
        </div>

        <div id="mx-wrap" ref={matrixWrapRef} style={{ overflow: 'auto', maxHeight: '70vh' }}>
          <table id="mx-table">
            <thead>
              <tr className="mx-grp-row" id="mx-grp-row">
                <th className="mx-grp-info mx-s1" style={{ position: 'sticky', left: 0, top: 0, zIndex: 13 }}>NAME</th>
                <th className="mx-grp-info mx-s2" style={{ position: 'sticky', left: 170, top: 0, zIndex: 13 }}>AGENCY</th>
                <th className="mx-grp-info mx-s3" style={{ position: 'sticky', left: 260, top: 0, zIndex: 13 }}>CSCS</th>
                {visibleGroups.map((g) => (
                  <th key={g.label} className={g.cssClass} colSpan={g.cols.length}>{g.label}</th>
                ))}
                <th className="mx-grp-info" style={{ whiteSpace: 'nowrap' }}>ACTIONS</th>
              </tr>
              <tr className="mx-col-row" id="mx-col-row">
                <th className={`mx-s1 ${sortBy === 'name' ? 'mx-sorted' : ''}`} style={{ position: 'sticky', left: 0, top: 33, zIndex: 14, cursor: 'pointer', textAlign: 'left' }} onClick={() => toggleSort('name')} title="Sort by name">
                  Name / Trade <span style={{ opacity: sortBy === 'name' ? 1 : .4, fontSize: 9 }}>{sortArrow('name')}</span>
                </th>
                <th className="mx-s2" style={{ position: 'sticky', left: 170, top: 33, zIndex: 14 }}>Agency</th>
                <th className="mx-s3" style={{ position: 'sticky', left: 260, top: 33, zIndex: 14 }}>CSCS Expiry</th>
                {orderedTrainCols.map((c) => {
                  const isSorted = sortBy === c.key;
                  const stat = colStats[c.key];
                  return (
                    <th key={c.key} className={`mx-train-col ${isSorted ? 'mx-sorted' : ''}`} data-mxcol={c.key} onClick={() => toggleSort(c.key)} title={c.label}>
                      {c.label} <span style={{ opacity: isSorted ? 1 : .4, fontSize: 9 }}>{sortArrow(c.key)}</span>
                      {stat && stat.total > 0 ? (
                        <span className={`mx-pct ${stat.pct === 0 ? 'zero' : ''}`}>{stat.pct}%<span className="mx-pct-frac">· {stat.held}/{stat.total}</span></span>
                      ) : null}
                    </th>
                  );
                })}
                <th className="mx-train-col" style={{ whiteSpace: 'nowrap' }}>Actions</th>
              </tr>
            </thead>
            <tbody id="mx-tbody">
              {filtered.length === 0 ? (
                <tr><td colSpan={3 + trainCols.length + 1} style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>No records match your filters.</td></tr>
              ) : recentActive && recentMode === 'condensed' ? (
                filtered.map((w) => renderCondensedRow(w)).filter(Boolean)
              ) : groupedByProject ? (
                groupedByProject.flatMap((grp) => [
                  <tr key={`grp-${grp.projectId}`}>
                    <td colSpan={3 + trainCols.length + 1} style={{ background: 'rgba(232,164,39,.1)', padding: '5px 10px', fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 10, color: 'var(--accent)', borderBottom: '1px solid var(--border)' }}>
                      🏗 {grp.projectName} — {grp.workers.length} worker{grp.workers.length !== 1 ? 's' : ''}
                    </td>
                  </tr>,
                  ...grp.workers.map((w) => renderMxRow(w)),
                ])
              ) : filtered.map((w) => renderMxRow(w))}
            </tbody>
          </table>
        </div>

        <div className="mx-legend">
          <strong style={{ color: 'var(--text)' }}>Key:</strong>
          <span className="mx-leg-item"><span className="mx-leg-dot" style={{ background: '#22c55e' }}></span>Valid</span>
          <span className="mx-leg-item"><span className="mx-leg-dot" style={{ background: '#fbbf24' }}></span>Expiring &lt;90d</span>
          <span className="mx-leg-item"><span className="mx-leg-dot" style={{ background: '#ef4444' }}></span>Expired</span>
          <span className="mx-leg-item"><span className="mx-leg-dot" style={{ background: '#ef4444', boxShadow: '0 0 0 1px #fff inset' }}></span>Missing (mandatory)</span>
          <span className="mx-leg-item"><span className="mx-leg-dot" style={{ background: '#484f58' }}></span>Not held</span>
          <span style={{ marginLeft: 'auto', fontSize: 10, opacity: .5 }}>Dates shown = expiry date</span>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, marginTop: 4 }}>
        <div className="sec-title" style={{ margin: 0 }}>Role Requirements</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {isInternal ? <button className="btn btn-ghost btn-sm" onClick={() => { setAddReqInitial(null); setAddReqOpen(true); }}>+ Add Requirement</button> : null}
          {isInternal ? <button className="btn btn-ghost btn-sm" onClick={() => { setRoleMgrEditRole(null); setRoleMgrOpen(true); }}>⚙ Edit All Roles</button> : null}
        </div>
      </div>

      <div className="g2" id="role-cards">
        {roleReqs.map((r) => {
          const rolesList = [r.role];
          const totalForRole = workers.filter((w) => (w.roles?.length ? w.roles : [w.role]).some((wr) => rolesList.includes(wr))).length;
          return (
            <div key={r.role} className="card">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 12 }}>{r.role}</div>
                {isInternal ? (
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn btn-ghost btn-xs" style={{ fontSize: 9 }} onClick={() => { setAddReqInitial({ role: r.role, category: 'mandatory' }); setAddReqOpen(true); }}>+ Mandatory</button>
                    <button className="btn btn-ghost btn-xs" style={{ fontSize: 9, color: 'var(--info)', borderColor: 'var(--info)' }} onClick={() => { setAddReqInitial({ role: r.role, category: 'beneficial' }); setAddReqOpen(true); }}>+ Beneficial</button>
                    <button className="btn btn-ghost btn-xs" style={{ fontSize: 9 }} onClick={() => { setRoleMgrEditRole(r.role); setRoleMgrOpen(true); }}>✏ Edit</button>
                  </div>
                ) : null}
              </div>
              <div style={{ fontSize: 9, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 4 }}>Mandatory ({r.mandatory.length})</div>
              {r.mandatory.length ? r.mandatory.map((cert) => {
                const cnt = workers.filter((w) => (w.roles?.length ? w.roles : [w.role]).includes(r.role) && w.certs.find((c) => c.type === cert && c.status !== 'expired')).length;
                return (
                  <div key={cert} style={{ fontSize: 11, padding: '3px 0', borderBottom: '1px solid rgba(30,42,64,.4)', display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ color: 'var(--accent)' }}>●</span>
                    <span style={{ flex: 1 }}>{cert}</span>
                    <span style={{ fontSize: 9, color: 'var(--muted)' }}>{cnt}/{totalForRole}</span>
                    {isInternal ? <button onClick={() => removeReq(r.role, 'mandatory', cert)} style={{ background: 'none', border: 'none', color: 'var(--faint)', cursor: 'pointer', fontSize: 11, lineHeight: 1, padding: '0 2px' }} title="Remove">✕</button> : null}
                  </div>
                );
              }) : <div style={{ fontSize: 10, color: 'var(--faint)', padding: '4px 0' }}>No mandatory requirements</div>}
              <div style={{ fontSize: 9, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--info)', margin: '8px 0 4px' }}>Beneficial ({r.beneficial.length})</div>
              {r.beneficial.length ? r.beneficial.map((cert) => (
                <div key={cert} style={{ fontSize: 11, padding: '3px 0', borderBottom: '1px solid rgba(30,42,64,.4)', display: 'flex', gap: 6, alignItems: 'center', color: 'var(--muted)' }}>
                  <span style={{ color: 'var(--info)' }}>○</span>
                  <span style={{ flex: 1 }}>{cert}</span>
                  {isInternal ? <button onClick={() => removeReq(r.role, 'beneficial', cert)} style={{ background: 'none', border: 'none', color: 'var(--faint)', cursor: 'pointer', fontSize: 11, lineHeight: 1, padding: '0 2px' }} title="Remove">✕</button> : null}
                </div>
              )) : <div style={{ fontSize: 10, color: 'var(--faint)', padding: '4px 0' }}>No beneficial requirements</div>}
            </div>
          );
        })}
      </div>

      {addCertOpen ? <AddCertModal onClose={() => setAddCertOpen(false)} /> : null}
      {addCertFor ? <AddCertModal workerId={addCertFor.workerId} certType={addCertFor.certType} onClose={() => setAddCertFor(null)} /> : null}
      {editCert ? <EditCertModal onClose={() => setEditCert(null)} certId={editCert.certId} workerName={editCert.workerName} initial={editCert.initial} /> : null}
      {detailId ? <WorkerDetail workerId={detailId} onClose={() => setDetailId(null)} /> : null}
      {shareWorkerId ? <ShareTrainingModal workerId={shareWorkerId} onClose={() => setShareWorkerId(null)} /> : null}
      {notifyOpen ? <CertNotifyModal onClose={() => setNotifyOpen(false)} /> : null}
      {notifyMissingOpen ? <CertNotifyModal onClose={() => setNotifyMissingOpen(false)} mode="missing" /> : null}
      {roleMgrOpen ? <RoleManagerModal onClose={() => { setRoleMgrOpen(false); setRoleMgrEditRole(null); }} initialEditRole={roleMgrEditRole ?? undefined} /> : null}
      {exportOpen ? <TrainExportModal onClose={() => setExportOpen(false)} descriptor={descriptor} visibleCount={filtered.length} totalWorkers={workers.length} /> : null}
      {addReqOpen ? <AddRoleReqModal onClose={() => { setAddReqOpen(false); setAddReqInitial(null); }} initialRole={addReqInitial?.role} initialCategory={addReqInitial?.category} /> : null}
      {importOpen ? <UploadModal entity="training" onClose={() => setImportOpen(false)} /> : null}
    </div>
  );
}
